import type {
  CreateLotStorageAssignmentDto,
  LotStorageAssignmentResponseDto,
  LotStorageContentCountsResponseDto,
} from '@prospero/shared';
import { inject } from 'inversify';
import { Injectable } from '../../decorators/injectable';
import { HttpService, type IHttpService } from '../http/http.service';
import type {
  ILotStorageService,
  LotStorageContentsResponse,
  LotStorageSuggestionResponseDto,
} from './lot-storage.types';

@Injectable()
export class LotStorageService implements ILotStorageService {
  constructor(@inject(HttpService) private readonly httpService: IHttpService) {}

  async getSuggestions(deliveryNoteId: string): Promise<LotStorageSuggestionResponseDto[]> {
    const params = new URLSearchParams({ deliveryNoteId });
    return this.httpService.get<LotStorageSuggestionResponseDto[]>(
      `lot-storage/suggestions?${params.toString()}`,
    );
  }

  async assign(dto: CreateLotStorageAssignmentDto): Promise<LotStorageAssignmentResponseDto> {
    return this.httpService.post<LotStorageAssignmentResponseDto>('lot-storage/assignments', dto);
  }

  async end(assignmentId: string): Promise<void> {
    await this.httpService.post<void>(`lot-storage/assignments/${assignmentId}/end`);
  }

  async getHistory(lotId: string): Promise<LotStorageAssignmentResponseDto[]> {
    return this.httpService.get<LotStorageAssignmentResponseDto[]>(
      `lot-storage/assignments?lotId=${lotId}`,
    );
  }

  async getContents(
    location: { equipmentId?: string; cleaningAreaId?: string },
    page: number,
    pageSize: number,
  ): Promise<LotStorageContentsResponse> {
    const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
    if (location.equipmentId) {
      params.set('equipmentId', location.equipmentId);
    }
    if (location.cleaningAreaId) {
      params.set('cleaningAreaId', location.cleaningAreaId);
    }
    return this.httpService.get<LotStorageContentsResponse>(
      `lot-storage/contents?${params.toString()}`,
    );
  }

  async getContentCounts(): Promise<LotStorageContentCountsResponseDto> {
    return this.httpService.get<LotStorageContentCountsResponseDto>('lot-storage/contents/counts');
  }
}
